import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useGlobalState } from '../../utils/GlobalState';
import { ArrowLeft, Trash2 } from "lucide-react";

const MURA_TYPE_DATA = {
  pressing_light_leakage: { id: '0', color: '#FF4500' },
  dark_state_mura: { id: '1', color: '#4682B4' },
  clouding: { id: '2', color: '#9932CC' },
  wavy_mura: { id: '3', color: '#FFD700' },
  lamp_shades: { id: '4', color: '#32CD32' },
  light_bract: { id: '5', color: '#FF6347' },
  grid_mura: { id: '6', color: '#708090' },
  corner_shadow: { id: '7', color: '#FFA500' },
  bright_side_around: { id: '8', color: '#000080' },
  dark_spot: { id: '9', color: '#A52A2A' },
  bright_spot: { id: '10', color: '#9370DB' },
  horizontal_band: { id: '11', color: '#3CB371' },
  vertical_band: { id: '12', color: '#00BFFF' },
  horizontal_line: { id: '13', color: '#FF69B4' },
  vertical_line: { id: '14', color: '#A9A9A9' },
  other: { id: '15', color: '#DC143C' },
};

const getNextMuraTypeId = () => {
  const existingIds = Object.values(MURA_TYPE_DATA).map(data => parseInt(data.id));
  return Math.max(...existingIds, 15) + 1;
};

const CUSTOM_COLORS = [
  '#FF1493', '#00CED1', '#ADFF2F', '#FF4500', '#8A2BE2',
  '#20B2AA', '#FF6347', '#9ACD32', '#BA55D3', '#0000FA9A',
];

const getUniqueColor = (existingColors) => {
  const usedColors = new Set(existingColors);
  for (const color of CUSTOM_COLORS) {
    if (!usedColors.has(color)) {
      return color;
    }
  }
  return `#${Math.floor(Math.random() * 16777215).toString(16)}`;
};

const generateRectHash = (rect) => {
  return `${rect.muraType}|${rect.x.toFixed(2)}|${rect.y.toFixed(2)}|${rect.width.toFixed(2)}|${rect.height.toFixed(2)}`;
};

const MarkRelabelingRegion = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { state, updateState } = useGlobalState();
  const [imageSrc, setImageSrc] = useState("");
  const [selectedMuraType, setSelectedMuraType] = useState("");
  const [selectedColor, setSelectedColor] = useState("");
  const [rectangles, setRectangles] = useState([]);
  const [isDrawing, setIsDrawing] = useState(false);
  const [startCoords, setStartCoords] = useState(null);
  const [selectedRectIndex, setSelectedRectIndex] = useState(null);
  const [mousePosition, setMousePosition] = useState(null);
  const [resizingHandle, setResizingHandle] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const canvasRef = useRef(null);
  const textareaRef = useRef(null);
  const padding = 50;
  const [isInsideImage, setIsInsideImage] = useState(true);
  const computedIssueType = selectedMuraType.toLowerCase().trim().replace(/\s+/g, " ") === "pressing light leakage" ? "LightLeakage" : "MuraLabeling";
  const hasRectBeauty = rectangles.length > 0;
  const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0 });
  const [canvasTransform, setCanvasTransform] = useState({
    scale: 1,
    offsetX: 0,
    offsetY: 0,
    scaledWidth: 0,
    scaledHeight: 0,
  });
  const [customMuraTypes, setCustomMuraTypes] = useState({});
  const [processedRectHashes, setProcessedRectHashes] = useState(new Set());
  const HANDLE_SIZE = 20;

  useEffect(() => {
    const suggestion = location.state?.suggestion || state.suggestion || "";
    if (suggestion && !state.suggestion) {
      updateState({ suggestion });
    }
  }, [location.state, state.suggestion, updateState]);

  useEffect(() => {
  const handleBeforeUnload = () => {
    setRectangles([]);
    setProcessedRectHashes(new Set());
    try {
      const muraData = JSON.parse(localStorage.getItem("muraData") || "{}");
      const updatedMuraData = {
        ...muraData,
        rectangles: [],
        yoloAnnotations: [],
        processedRectHashes: [],
      };
      localStorage.setItem("muraData", JSON.stringify(updatedMuraData));
      localStorage.removeItem("muraRectangles");
    } catch (e) {
      console.warn("Failed to update muraData in localStorage:", e);
      localStorage.setItem("muraData", JSON.stringify({
        rectangles: [],
        yoloAnnotations: [],
        processedRectHashes: [],
      }));
      localStorage.removeItem("muraRectangles");
    }
  };

  window.addEventListener('beforeunload', handleBeforeUnload);

  return () => {
    window.removeEventListener('beforeunload', handleBeforeUnload);
  };
}, []);

  useEffect(() => {
    return () => {
      if (canvasRef.current) {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext("2d");
        ctx.clearRect(0, 0, canvas.width, canvas.height);
      }
    };
  }, []);

const loadImageAndAnnotations = () => {
  const analyzedImage = location.state?.analysisResult?.data?.predicted_image_url || "";
  const storedImage = state.uploadedImage || location.state?.uploadedImage || "";
  const imageToUse = analyzedImage || storedImage;
  const recordedEntries = location.state?.recordedEntries || [];
  const passedCustomMuraTypes = location.state?.customMuraTypes || {};
  const yoloAnnotations = location.state?.yoloAnnotations || [];
 
  setCustomMuraTypes(passedCustomMuraTypes);

  const existingColors = Object.values(MURA_TYPE_DATA).map(data => data.color);
  Object.entries(passedCustomMuraTypes).forEach(([muraType, id]) => {
    if (!MURA_TYPE_DATA[muraType]) {
      MURA_TYPE_DATA[muraType] = {
        id: String(id),
        color: getUniqueColor(existingColors),
      };
      existingColors.push(MURA_TYPE_DATA[muraType].color);  
    }
  });

  if (imageToUse) {
    const img = new Image();
    img.src = imageToUse;

    img.onerror = () => {
      console.error("Failed to load image:", imageToUse);
      setImageSrc("");
    };

    img.onload = () => {   
      setImageDimensions({ width: img.width, height: img.height });

      const canvasWidth = 2000;
      const canvasHeight = 1250;
      const availableWidth = canvasWidth - padding * 2;
      const availableHeight = canvasHeight - padding * 2;
      const scale = Math.min(availableWidth / img.width, availableHeight / img.height);
      const scaledWidth = img.width * scale;
      const scaledHeight = img.height * scale;
      const offsetX = padding + (availableWidth - scaledWidth) / 2;
      const offsetY = padding + (availableHeight - scaledHeight) / 2;
      const canvasTransform = { scale, offsetX, offsetY, scaledWidth, scaledHeight };
      setCanvasTransform(canvasTransform);
      let loadedRectangles = [];
      if (recordedEntries.length > 0) {
        loadedRectangles = recordedEntries
          .filter(entry => entry.rectangles && Array.isArray(entry.rectangles) && entry.muraType)
          .flatMap(entry => entry.rectangles)
          .filter(rect => rect && MURA_TYPE_DATA[rect.muraType]); 
      }

      let yoloRectangles = [];
      if (yoloAnnotations.length > 0) {
        yoloRectangles = convertYOLOToRectangles(yoloAnnotations, img.width, img.height);
      
      }

      let storedRectangles = [];
      try {
        const muraData = JSON.parse(localStorage.getItem("muraData") || "{}");
   
        if (muraData.rectangles && Array.isArray(muraData.rectangles)) {
          storedRectangles = muraData.rectangles.filter(rect => rect && rect.muraType && MURA_TYPE_DATA[rect.muraType]);
     
        }
      } catch (e) {
        console.warn("Failed to parse muraData.rectangles:", e);
      }

      let allRectangles = [];
      const seenHashes = new Set();
      const allSources = [...storedRectangles, ...loadedRectangles, ...yoloRectangles];
    

      allRectangles = allSources.reduce((acc, rect) => {
        if (!rect || !rect.muraType || !MURA_TYPE_DATA[rect.muraType] || !Number.isFinite(rect.x) || 
            !Number.isFinite(rect.y) || !Number.isFinite(rect.width) || !Number.isFinite(rect.height)) {
          console.warn("Skipping invalid rectangle:", rect);
          return acc;
        }

        const hash = generateRectHash(rect);
        if (seenHashes.has(hash)) {
          console.warn("Skipping duplicate rectangle:", rect);
          return acc;
        }
        seenHashes.add(hash);

        acc.push({
          ...rect,
          color: MURA_TYPE_DATA[rect.muraType].color,
          muraTypeIndex: Number(MURA_TYPE_DATA[rect.muraType].id),
        });
        return acc;
      }, []);
     

      setRectangles(allRectangles);
      setImageSrc(imageToUse);
      setSelectedRectIndex(null);
      setIsDrawing(false);
      setStartCoords(null);
   

      try {
        const muraData = JSON.parse(localStorage.getItem("muraData") || "{}");
        const existingHashes = new Set(muraData.processedRectHashes || []);
        const updatedHashes = new Set([...existingHashes, ...seenHashes]);
        setProcessedRectHashes(updatedHashes);
      
      } catch (e) {
        console.warn("Failed to update processedRectHashes:", e);
        setProcessedRectHashes(seenHashes);
   
      }

      const selectedMuraTypeFromState = location.state?.selectedMuraType;
      const muraType = location.state?.muraType || "";
      const customMuraType = location.state?.customMuraType || "";
 

      if (selectedMuraTypeFromState && MURA_TYPE_DATA[selectedMuraTypeFromState]) {
        setSelectedMuraType(selectedMuraTypeFromState);
        setSelectedColor(MURA_TYPE_DATA[selectedMuraTypeFromState].color);
       
      } else if (customMuraType && passedCustomMuraTypes[customMuraType.toLowerCase().replace(/\s+/g, "_")]) {
        const formattedCustomType = customMuraType.toLowerCase().replace(/\s+/g, "_");
        setSelectedMuraType(formattedCustomType);
        setSelectedColor(MURA_TYPE_DATA[formattedCustomType].color);
      } else if (muraType && MURA_TYPE_DATA[muraType.toLowerCase().replace(/\s+/g, "_")]) {
        const formattedType = muraType.toLowerCase().replace(/\s+/g, "_");
        setSelectedMuraType(formattedType);
        setSelectedColor(MURA_TYPE_DATA[formattedType].color);
      } else if (customMuraType) {
        const formattedCustomType = customMuraType.toLowerCase().replace(/\s+/g, "_");
        if (!MURA_TYPE_DATA[formattedCustomType]) {
          const newId = passedCustomMuraTypes[formattedCustomType] || String(getNextMuraTypeId());
          MURA_TYPE_DATA[formattedCustomType] = {
            id: newId,
            color: getUniqueColor(existingColors),
          };
          setCustomMuraTypes(prev => ({
            ...prev,
            [formattedCustomType]: newId,
          }));
         
        }
        setSelectedMuraType(formattedCustomType);
        setSelectedColor(MURA_TYPE_DATA[formattedCustomType].color);
      } else {
        setSelectedMuraType("other");
        setSelectedColor(MURA_TYPE_DATA["other"].color);
      }
    };
  } else {
    console.error("No valid image source provided.");
  }
};

  useEffect(() => {
    loadImageAndAnnotations();
  }, [location.state]);

  const convertYOLOToRectangles = (yoloAnnotations, imgWidth, imgHeight) => {
    if (typeof yoloAnnotations === "string") {
      yoloAnnotations = yoloAnnotations
        .trim()
        .split("\n")
        .map((line) => line.split(" ").map(Number));
    }
    if (!Array.isArray(yoloAnnotations)) {
      console.warn("Invalid YOLO annotations format:", yoloAnnotations);
      return [];
    }
    const rectangles = yoloAnnotations
      .map((ann, index) => {
        if (!Array.isArray(ann) || ann.length < 5) {
          console.warn(`Skipping invalid YOLO annotation at index ${index}:`, ann);
          return null;
        }
        const [classIndex, x_center, y_center, width, height] = ann;
        const muraType =
          Object.keys(MURA_TYPE_DATA).find((key) => MURA_TYPE_DATA[key].id === String(classIndex)) || "other";
        return {
          x: (x_center - width / 2) * imgWidth,
          y: (y_center - height / 2) * imgHeight,
          width: width * imgWidth,
          height: height * imgHeight,
          color: MURA_TYPE_DATA[muraType].color,
          muraType,
          muraTypeIndex: Number(classIndex),
        };
      })
      .filter((rect) => rect !== null);
    return rectangles;
  };

  useEffect(() => {
    if (imageSrc && canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d");
      const img = new Image();

      img.onerror = () => {
        console.error("Failed to load image in canvas:", imageSrc);
        setImageSrc("");
      };
      img.src = imageSrc;
      img.onload = () => {
        const fixedWidth = 2000;
        const fixedHeight = 1250;

        if (canvas.width !== fixedWidth || canvas.height !== fixedHeight) {
          canvas.width = fixedWidth;
          canvas.height = fixedHeight;
        }

        redrawCanvas(ctx, img, padding);
      };
    } else {
      console.warn("Canvas or imageSrc not available:", { imageSrc, canvasRef: !!canvasRef.current });
    }
  }, [imageSrc, rectangles, selectedRectIndex, mousePosition, isInsideImage, isDrawing, startCoords, selectedColor, padding]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const options = { passive: false };
    const touchStart = (e) => handleTouchStart(e);
    const touchMove = (e) => handleTouchMove(e);
    const touchEnd = (e) => handleTouchEnd(e);
    const touchCancel = (e) => handleTouchCancel(e);
    canvas.addEventListener("touchstart", touchStart, options);
    canvas.addEventListener("touchmove", touchMove, options);
    canvas.addEventListener("touchend", touchEnd, options);
    canvas.addEventListener("touchcancel", touchCancel, options);
    return () => {
      canvas.removeEventListener("touchstart", touchStart);
      canvas.removeEventListener("touchmove", touchMove);
      canvas.removeEventListener("touchend", touchEnd);
      canvas.removeEventListener("touchcancel", touchCancel);
    };
  }, [isDrawing, isDragging, resizingHandle, imageSrc, mousePosition, startCoords, selectedMuraType, selectedColor]);

  const redrawCanvas = (ctx, img, padding = 0) => {
    const canvas = ctx.canvas;
    const { scale, offsetX, offsetY, scaledWidth, scaledHeight } = canvasTransform;

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, offsetX, offsetY, scaledWidth, scaledHeight);

    if (isDrawing && startCoords && mousePosition && isInsideImage) {
      const startX = startCoords.x;
      const startY = startCoords.y;
      const endX = (mousePosition.x - offsetX) / scale;
      const endY = (mousePosition.y - offsetY) / scale;

      const x = Math.min(startX, endX);
      const y = Math.min(startY, endY);
      const width = Math.abs(endX - startX);
      const height = Math.abs(endY - startY);

      ctx.strokeStyle = selectedColor;
      ctx.lineWidth = 8;
      ctx.lineJoin = "round";
      ctx.strokeRect(
        offsetX + x * scale,
        offsetY + y * scale,
        width * scale,
        height * scale
      );
    }

    drawRectangles(ctx, padding);
    drawCrosshair(ctx);
    drawCoordinates(ctx);
  };

  const drawRectangles = (ctx, padding = 0) => {
    ctx.save();
    const { scale, offsetX, offsetY } = canvasTransform;
    rectangles.forEach((rect, index) => {
      ctx.strokeStyle = index === selectedRectIndex ? "white" : rect.color;
      ctx.lineWidth = 5;
      ctx.lineJoin = "round";
      ctx.strokeRect(
        offsetX + rect.x * scale,
        offsetY + rect.y * scale,
        rect.width * scale,
        rect.height * scale
      );

      if (index === selectedRectIndex) {
        ctx.fillStyle = "rgba(0, 255, 0, 0.2)";
        ctx.fillRect(
          offsetX + rect.x * scale,
          offsetY + rect.y * scale,
          rect.width * scale,
          rect.height * scale
        );

        const handles = getResizeHandles(rect, padding);
        ctx.fillStyle = "white";
        handles.forEach(handle => {
          ctx.fillRect(handle.x, handle.y, HANDLE_SIZE, HANDLE_SIZE);
        });
      }
    });
    ctx.restore();
  };

  const getResizeHandles = (rect, padding) => {
    if (!rect) return [];
    const { scale, offsetX, offsetY } = canvasTransform;
    const x = offsetX + rect.x * scale;
    const y = offsetY + rect.y * scale;
    const width = rect.width * scale;
    const height = rect.height * scale;
    return [
      { x: x - HANDLE_SIZE / 2, y: y - HANDLE_SIZE / 2, position: "nw" },
      { x: x + width - HANDLE_SIZE / 2, y: y - HANDLE_SIZE / 2, position: "ne" },
      { x: x - HANDLE_SIZE / 2, y: y + height - HANDLE_SIZE / 2, position: "sw" },
      { x: x + width - HANDLE_SIZE / 2, y: y + height - HANDLE_SIZE / 2, position: "se" },
      { x: x + width / 2 - HANDLE_SIZE / 2, y: y - HANDLE_SIZE / 2, position: "n" },
      { x: x + width / 2 - HANDLE_SIZE / 2, y: y + height - HANDLE_SIZE / 2, position: "s" },
      { x: x - HANDLE_SIZE / 2, y: y + height / 2 - HANDLE_SIZE / 2, position: "w" },
      { x: x + width - HANDLE_SIZE / 2, y: y + height / 2 - HANDLE_SIZE / 2, position: "e" },
    ];
  };

  const getCursorForHandle = (handlePosition) => {
    switch (handlePosition) {
      case "n":
      case "s":
        return "ns-resize";
      case "e":
      case "w":
        return "ew-resize";
      case "ne":
      case "sw":
        return "nesw-resize";
      case "nw":
      case "se":
        return "nwse-resize";
      default:
        return "move";
    }
  };

  const drawCrosshair = (ctx) => {
    if (!mousePosition) return;
    const gapSize = 35;
    const centerX = mousePosition.x;
    const centerY = mousePosition.y;
    const dotRadius = 8;
    ctx.save();
    ctx.strokeStyle = (isDrawing || isDragging || resizingHandle) ? "white" : "white";
    ctx.lineWidth = (isDrawing || isDragging || resizingHandle) ? 8 : 5;
    ctx.setLineDash([]);
    ctx.beginPath();
    ctx.moveTo(centerX, 0);
    ctx.lineTo(centerX, centerY - gapSize);
    ctx.moveTo(centerX, centerY + gapSize);
    ctx.lineTo(centerX, ctx.canvas.height);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(0, centerY);
    ctx.lineTo(centerX - gapSize, centerY);
    ctx.moveTo(centerX + gapSize, centerY);
    ctx.lineTo(ctx.canvas.width, centerY);
    ctx.stroke();
    ctx.beginPath();
    ctx.fillStyle = "white";
    ctx.arc(centerX, centerY, dotRadius, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  };

  const drawCoordinates = (ctx) => {
    if (!mousePosition) return;
    const { x, y } = mousePosition;
    const { scale, offsetX, offsetY } = canvasTransform;
    const banOffsetX = 45;
    const banOffsetY = 45;
    const banRadius = 20;
    const banX = x + banOffsetX;
    const banY = y + banOffsetY;
    if (!isInsideImage) {
      ctx.save();
      ctx.beginPath();
      ctx.strokeStyle = "white";
      ctx.lineWidth = 6;
      ctx.arc(banX, banY, banRadius, 0, Math.PI * 2);
      ctx.stroke();
      const crossLength = 15;
      ctx.beginPath();
      ctx.lineWidth = 6;
      ctx.moveTo(banX - crossLength, banY - crossLength);
      ctx.lineTo(banX + crossLength, banY + crossLength);
      ctx.stroke();
      ctx.restore();
    } else {
      ctx.save();
      const imgX = ((x - offsetX) / scale).toFixed(0);
      const imgY = ((y - offsetY) / scale).toFixed(0);
      const text = `x: ${imgX}, y: ${imgY}`;
      const fontSize = 30;
      ctx.font = `bold ${fontSize}px Arial`;
      const paddingBox = 15;
      const textWidth = ctx.measureText(text).width;
      const boxWidth = textWidth + paddingBox * 2;
      const boxHeight = fontSize + paddingBox * 2;
      let boxX = x + banOffsetX;
      let boxY = y + banOffsetY;
      if (boxX + boxWidth > ctx.canvas.width) {
        boxX = x - boxWidth - banOffsetX;
      }
      if (boxY < 0) {
        boxY = y - banOffsetY;
      }
      if (boxY + boxHeight > ctx.canvas.height) {
        boxY = y - boxHeight - banOffsetY;
      }
      ctx.fillStyle = "rgba(0, 0, 0, 0.7)";
      ctx.fillRect(boxX, boxY, boxWidth, boxHeight);
      ctx.fillStyle = "white";
      ctx.textBaseline = "top";
      ctx.fillText(text, boxX + paddingBox, boxY + paddingBox);
      ctx.restore();
    }
  };

  const getHandleAtPosition = (x, y) => {
    if (selectedRectIndex === null || selectedRectIndex >= rectangles.length) return null;
    const rect = rectangles[selectedRectIndex];
    const handles = getResizeHandles(rect, padding);
    for (const handle of handles) {
      if (x >= handle.x && x <= handle.x + HANDLE_SIZE && y >= handle.y && y <= handle.y + HANDLE_SIZE) {
        return handle.position;
      }
    }
    return null;
  };

  const isCoordinateWithinImage = (imgX, imgY) => {
    return (
      imgX >= 0 &&
      imgY >= 0 &&
      imgX <= imageDimensions.width &&
      imgY <= imageDimensions.height
    );
  };

  const handleMouseDown = (e) => {
    if (!canvasRef.current) return;
    const { x, y } = getCanvasCoordinates(e.clientX, e.clientY);
    const { scale, offsetX, offsetY } = canvasTransform;
    const imgX = (x - offsetX) / scale;
    const imgY = (y - offsetY) / scale;
    if (!isCoordinateWithinImage(imgX, imgY)) {
      return;
    }
    const handlePosition = getHandleAtPosition(x, y);
    if (handlePosition) {
      setResizingHandle(handlePosition);
      return;
    }
    let clickedIndex = null;
    rectangles.forEach((rect, index) => {
      const rectX = offsetX + rect.x * scale;
      const rectY = offsetY + rect.y * scale;
      const rectWidth = rect.width * scale;
      const rectHeight = rect.height * scale;
      if (x >= rectX && x <= rectX + rectWidth && y >= rectY && y <= rectY + rectHeight) {
        clickedIndex = index;
      }
    });
    if (clickedIndex !== null) {
      setSelectedRectIndex(clickedIndex);
      setIsDragging(true);
      const rect = rectangles[clickedIndex];
      setDragOffset({
        x: imgX - rect.x,
        y: imgY - rect.y,
      });
      setSelectedColor(rect.color);
    } else {
      setIsDrawing(true);
      setStartCoords({ x: imgX, y: imgY });
      setSelectedRectIndex(null);
      setSelectedColor(MURA_TYPE_DATA[selectedMuraType]?.color || MURA_TYPE_DATA["other"].color);
    }
  };

  const handleMouseMove = (e) => {
    if (!canvasRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const { x, y } = getCanvasCoordinates(e.clientX, e.clientY);
    const { scale, offsetX, offsetY } = canvasTransform;
    setMousePosition({ x, y });
    const imgX = (x - offsetX) / scale;
    const imgY = (y - offsetY) / scale;
    const insideImage = isCoordinateWithinImage(imgX, imgY);
    setIsInsideImage(insideImage);
    if (isDrawing && !insideImage) {
      setIsDrawing(false);
      setStartCoords(null);
      const img = new Image();
      img.src = imageSrc;
      img.onload = () => redrawCanvas(ctx, img, padding);
      return;
    }
    if (selectedRectIndex !== null && !resizingHandle && !isDrawing && !isDragging) {
      const handlePosition = getHandleAtPosition(x, y);
      canvas.style.cursor = handlePosition ? getCursorForHandle(handlePosition) : "default";
    }
    if (resizingHandle && selectedRectIndex !== null) {
      const rect = rectangles[selectedRectIndex];
      const newRect = { ...rect };
      switch (resizingHandle) {
        case "nw":
          newRect.width = rect.x + rect.width - imgX;
          newRect.height = rect.y + rect.height - imgY;
          newRect.x = imgX;
          newRect.y = imgY;
          break;
        case "ne":
          newRect.width = imgX - rect.x;
          newRect.height = rect.y + rect.height - imgY;
          newRect.y = imgY;
          break;
        case "sw":
          newRect.width = rect.x + rect.width - imgX;
          newRect.height = imgY - rect.y;
          newRect.x = imgX;
          break;
        case "se":
          newRect.width = imgX - rect.x;
          newRect.height = imgY - rect.y;
          break;
        case "n":
          newRect.height = rect.y + rect.height - imgY;
          newRect.y = imgY;
          break;
        case "s":
          newRect.height = imgY - rect.y;
          break;
        case "e":
          newRect.width = imgX - rect.x;
          break;
        case "w":
          newRect.width = rect.x + rect.width - imgX;
          newRect.x = imgX;
          break;
      }
      if (newRect.width < 5 / scale) newRect.width = 5 / scale;
      if (newRect.height < 5 / scale) newRect.height = 5 / scale;
      const newRectangles = [...rectangles];
      newRectangles[selectedRectIndex] = newRect;
      setRectangles(newRectangles);
    }
    if (isDragging && selectedRectIndex !== null) {
      const newRectangles = [...rectangles];
      const rect = newRectangles[selectedRectIndex];
      const newX = imgX - dragOffset.x;
      const newY = imgY - dragOffset.y;
      rect.x = Math.max(0, Math.min(newX, imageDimensions.width - rect.width));
      rect.y = Math.max(0, Math.min(newY, imageDimensions.height - rect.height));
      setRectangles(newRectangles);
    }
    if (imageSrc) {
      const img = new Image();
      img.src = imageSrc;
      img.onload = () => redrawCanvas(ctx, img, padding);
    }
  };

  const handleMouseUp = (e) => {
    if (resizingHandle) {
      setResizingHandle(null);
      return;
    }
    if (isDragging) {
      setIsDragging(false);
      return;
    }
    if (!canvasRef.current || !isDrawing || !startCoords || !isInsideImage) {
      setIsDrawing(false);
      setStartCoords(null);
      return;
    }
    let imgX, imgY;
    if (e.clientX !== undefined && e.clientY !== undefined) {
      const { x, y } = getCanvasCoordinates(e.clientX, e.clientY);
      const { scale, offsetX, offsetY } = canvasTransform;
      imgX = (x - offsetX) / scale;
      imgY = (y - offsetY) / scale;
    } else if (mousePosition) {
      const { scale, offsetX, offsetY } = canvasTransform;
      imgX = (mousePosition.x - offsetX) / scale;
      imgY = (mousePosition.y - offsetY) / scale;
    } else {
      setIsDrawing(false);
      setStartCoords(null);
      return;
    }
    if (isCoordinateWithinImage(imgX, imgY)) {
      if (Math.abs(imgX - startCoords.x) > 5 / canvasTransform.scale && Math.abs(imgY - startCoords.y) > 5 / canvasTransform.scale) {
        const muraTypeData = MURA_TYPE_DATA[selectedMuraType] || MURA_TYPE_DATA["other"];
        const newRectangle = {
          x: Math.min(startCoords.x, imgX),
          y: Math.min(startCoords.y, imgY),
          width: Math.abs(imgX - startCoords.x),
          height: Math.abs(imgY - startCoords.y),
          color: selectedColor,
          muraType: selectedMuraType,
          muraTypeIndex: Number(muraTypeData.id),
        };
        setRectangles((prev) => [...prev, newRectangle]);
        setSelectedRectIndex(rectangles.length);
      }
    }
    setIsDrawing(false);
    setStartCoords(null);
  };

  const handleDelete = () => {
    if (selectedRectIndex !== null) {
      const deletedRect = rectangles[selectedRectIndex];
      setRectangles(rectangles.filter((_, i) => i !== selectedRectIndex));
      setSelectedRectIndex(null);
      setSelectedColor(MURA_TYPE_DATA[selectedMuraType]?.color || MURA_TYPE_DATA["other"].color);
      const hash = generateRectHash(deletedRect);
      setProcessedRectHashes(prev => {
        const newSet = new Set(prev);
        newSet.delete(hash);
        return newSet;
      });
    }
  };

  const convertToYOLOFormat = (rectangles, imgWidth, imgHeight) => {
    const yoloAnnotations = rectangles.map((rect) => {
      const x_center = (rect.x + rect.width / 2) / imgWidth;
      const y_center = (rect.y + rect.height / 2) / imgHeight;
      const width = rect.width / imgWidth;
      const height = rect.height / imgHeight;
      const classIndex = rect.muraTypeIndex !== undefined ? Number(rect.muraTypeIndex) : Number(MURA_TYPE_DATA["other"].id);
      const annotation = [classIndex, x_center, y_center, width, height];
      return annotation;
    });
    return yoloAnnotations;
  };

  const handleNext = async () => {
    if (!canvasRef.current || !imageSrc) {
      console.error("Cannot navigate: Canvas or image not available.");
      return;
    }

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const img = new Image();
    img.src = imageSrc;
    await new Promise(resolve => {
      img.onload = () => {
        resolve();
      };
      img.onerror = () => console.error("Failed to load image for navigation:", imageSrc);
    });

    const wasDrawing = isDrawing;
    const wasDragging = isDragging;
    const wasResizing = resizingHandle;
    const tempMousePos = mousePosition;

    setIsDrawing(false);
    setIsDragging(false);
    setResizingHandle(null);
    setMousePosition(null);

    let existingYoloAnnotations = [];
    try {
      const muraData = JSON.parse(localStorage.getItem("muraData") || "{}");
      if (muraData.yoloAnnotations && Array.isArray(muraData.yoloAnnotations)) {
        existingYoloAnnotations = muraData.yoloAnnotations;
      } 
    } catch (e) {
      console.warn("Failed to parse existing yoloAnnotations from muraData:", e);
    }

    let textareaYoloAnnotations = [];
    try {
      const textareaContent = textareaRef?.current?.value || "";
      if (textareaContent) {
        const lines = textareaContent.trim().split("\n");
        textareaYoloAnnotations = lines
          .map(line => {
            const values = line.trim().split(/\s+/).map(Number);
            if (values.length === 5 && values.every(v => !isNaN(v))) {
              return values;
            }
            return null;
          })
          .filter(ann => ann !== null);
      } 
    } catch (e) {
      console.warn("Failed to parse YOLO annotations from textarea:", e);
    }
    const allYoloAnnotations = [...existingYoloAnnotations, ...textareaYoloAnnotations];
    const allRectangles = allYoloAnnotations.map(ann => {
      const classIndex = ann[0];
      const muraType = Object.keys(MURA_TYPE_DATA).find(key => MURA_TYPE_DATA[key].id === String(classIndex)) || "other";
      const x_center = ann[1] * imageDimensions.width;
      const y_center = ann[2] * imageDimensions.height;
      const width = ann[3] * imageDimensions.width;
      const height = ann[4] * imageDimensions.height;
      const rect = {
        x: x_center - width / 2,
        y: y_center - height / 2,
        width,
        height,
        muraType,
        color: MURA_TYPE_DATA[muraType]?.color || "#FF0000",
        muraTypeIndex: Number(classIndex),
      };
      return rect;
    });

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, canvasTransform.offsetX, canvasTransform.offsetY, canvasTransform.scaledWidth, canvasTransform.scaledHeight);
    [...rectangles, ...allRectangles].forEach(rect => {
      ctx.strokeStyle = rect.color;
      ctx.lineWidth = 5;
      ctx.lineJoin = "round";
      ctx.strokeRect(
        canvasTransform.offsetX + rect.x * canvasTransform.scale,
        canvasTransform.offsetY + rect.y * canvasTransform.scale,
        rect.width * canvasTransform.scale,
        rect.height * canvasTransform.scale
      );
    });

    const blob = await new Promise(resolve => {
      canvas.toBlob(resolve, "image/jpeg", 0.8);
    });

    setIsDrawing(wasDrawing);
    setIsDragging(wasDragging);
    setResizingHandle(wasResizing);
    setMousePosition(tempMousePos);

    const currentRectangles = rectangles.filter(rect => rect.muraType === selectedMuraType);
    const newRectangles = currentRectangles.filter(rect => {
      const hash = generateRectHash(rect);
      return !processedRectHashes.has(hash);
    });

    const updatedProcessedRectHashes = new Set(processedRectHashes);
    newRectangles.forEach(rect => {
      const hash = generateRectHash(rect);
      updatedProcessedRectHashes.add(hash);
    });

    const updatedRectangles = [...rectangles, ...allRectangles].map(rect => ({
      ...rect,
      muraTypeIndex: Number(MURA_TYPE_DATA[rect.muraType]?.id || MURA_TYPE_DATA["other"].id),
    }));

    const yoloAnnotations = convertToYOLOFormat(newRectangles, imageDimensions.width, imageDimensions.height);

    const muraData = {
      muraType: selectedMuraType,
      rectangles: updatedRectangles,
      imageDimensions,
      yoloAnnotations: [...allYoloAnnotations, ...yoloAnnotations],
      canvasTransform,
      processedRectHashes: Array.from(updatedProcessedRectHashes),
    };
    localStorage.setItem("muraData", JSON.stringify(muraData));

    const recordedEntries = location.state?.recordedEntries || [];
    const updatedRecordedEntries = recordedEntries.some(entry => entry.muraType === selectedMuraType)
      ? recordedEntries.map(entry => {
          if (entry.muraType === selectedMuraType) {
            return {
              ...entry,
              rectangles: updatedRectangles.filter(rect => rect.muraType === selectedMuraType),
            };
          }
          return entry;
        })
      : [
          ...recordedEntries,
          {
            muraType: selectedMuraType,
            rectangles: updatedRectangles.filter(rect => rect.muraType === selectedMuraType),
            rootCause: null,
            rootIssue: null,
          },
        ];

    const navigationRectangles = updatedRectangles.filter(rect => rect.muraType === selectedMuraType);
    navigate("/Issue-Rootcause", {
      state: {
        muraType: selectedMuraType,
        muraImage: URL.createObjectURL(blob),
        muraData,
        yoloAnnotations,
        uploadedImage: state?.uploadedImage || "",
        recordedEntries: updatedRecordedEntries,
        previouslySelectedTypes: location.state?.previouslySelectedTypes || [],
        selectedMuraTypes: location.state?.selectedMuraTypes || [],
        rectangles: navigationRectangles,
        customMuraTypes,
        customMuraType: location.state?.customMuraType || "",
        fromRootCauseLabeling: false,
        issueType: location.state?.issueType || computedIssueType,
        analysisResult: location.state?.analysisResult,
        suggestion: state.suggestion || location.state?.suggestion || "",
      },
    });
  };

  const handleTouchStart = (e) => {
    e.preventDefault();
    if (!canvasRef.current) return;
    const touch = e.touches[0];
    const { x, y } = getCanvasCoordinates(touch.clientX, touch.clientY);
    const { scale, offsetX, offsetY } = canvasTransform;
    const imgX = (x - offsetX) / scale;
    const imgY = (y - offsetY) / scale;
    setMousePosition({ x, y });
    if (!isCoordinateWithinImage(imgX, imgY)) {
      return;
    }
    if (selectedRectIndex !== null && selectedRectIndex < rectangles.length) {
      const handlePosition = getHandleAtPosition(x, y);
      if (handlePosition) {
        setResizingHandle(handlePosition);
        return;
      }
    }
    let clickedIndex = null;
    rectangles.forEach((rect, index) => {
      const rectX = offsetX + rect.x * scale;
      const rectY = offsetY + rect.y * scale;
      const rectWidth = rect.width * scale;
      const rectHeight = rect.height * scale;
      if (x >= rectX && x <= rectX + rectWidth && y >= rectY && y <= rectY + rectHeight) {
        clickedIndex = index;
      }
    });
    if (clickedIndex !== null) {
      setSelectedRectIndex(clickedIndex);
      setIsDragging(true);
      setDragOffset({
        x: imgX - rectangles[clickedIndex].x,
        y: imgY - rectangles[clickedIndex].y,
      });
      setSelectedColor(rectangles[clickedIndex].color);
    } else {
      setIsDrawing(true);
      setStartCoords({ x: imgX, y: imgY });
      setSelectedRectIndex(null);
      setSelectedColor(MURA_TYPE_DATA[selectedMuraType]?.color || MURA_TYPE_DATA["other"].color);
    }
  };

  const handleTouchMove = (e) => {
    if (isDrawing || isDragging || resizingHandle) {
      e.preventDefault();
    }
    if (!canvasRef.current) return;
    const touch = e.touches[0];
    const { x, y } = getCanvasCoordinates(touch.clientX, touch.clientY);
    const { scale, offsetX, offsetY } = canvasTransform;
    const imgX = (x - offsetX) / scale;
    const imgY = (y - offsetY) / scale;
    setMousePosition({ x, y });
    const insideImage = isCoordinateWithinImage(imgX, imgY);
    setIsInsideImage(insideImage);
    if (isDrawing && !insideImage) {
      setIsDrawing(false);
      setStartCoords(null);
      const ctx = canvasRef.current.getContext("2d");
      const img = new Image();
      img.src = imageSrc;
      img.onload = () => redrawCanvas(ctx, img, padding);
      return;
    }
    if (selectedRectIndex !== null && !resizingHandle && !isDrawing && !isDragging) {
      const handlePosition = getHandleAtPosition(x, y);
      canvasRef.current.style.cursor = handlePosition ? getCursorForHandle(handlePosition) : "default";
    }
    if (resizingHandle && selectedRectIndex !== null) {
      const rect = rectangles[selectedRectIndex];
      const newRect = { ...rect };
      switch (resizingHandle) {
        case "nw":
          newRect.width = rect.x + rect.width - imgX;
          newRect.height = rect.y + rect.height - imgY;
          newRect.x = imgX;
          newRect.y = imgY;
          break;
        case "ne":
          newRect.width = imgX - rect.x;
          newRect.height = rect.y + rect.height - imgY;
          newRect.y = imgY;
          break;
        case "sw":
          newRect.width = rect.x + rect.width - imgX;
          newRect.height = imgY - rect.y;
          newRect.x = imgX;
          break;
        case "se":
          newRect.width = imgX - rect.x;
          newRect.height = imgY - rect.y;
          break;
        case "n":
          newRect.height = rect.y + rect.height - imgY;
          newRect.y = imgY;
          break;
        case "s":
          newRect.height = imgY - rect.y;
          break;
        case "e":
          newRect.width = imgX - rect.x;
          break;
        case "w":
          newRect.width = rect.x + rect.width - imgX;
          newRect.x = imgX;
          break;
      }
      if (newRect.width < 5 / scale) newRect.width = 5 / scale;
      if (newRect.height < 5 / scale) newRect.height = 5 / scale;
      const newRectangles = [...rectangles];
      newRectangles[selectedRectIndex] = newRect;
      setRectangles(newRectangles);
    }
    if (isDragging && selectedRectIndex !== null) {
      const newRectangles = [...rectangles];
      const rect = newRectangles[selectedRectIndex];
      const newX = imgX - dragOffset.x;
      const newY = imgY - dragOffset.y;
      rect.x = Math.max(0, Math.min(newX, imageDimensions.width - rect.width));
      rect.y = Math.max(0, Math.min(newY, imageDimensions.height - rect.height));
      setRectangles(newRectangles);
    }
    if (isDrawing && startCoords) {
      const ctx = canvasRef.current.getContext("2d");
      const img = new Image();
      img.src = imageSrc;
      img.onload = () => redrawCanvas(ctx, img, padding);
    }
  };

  const handleTouchEnd = (e) => {
    e.preventDefault();
    if (resizingHandle) {
      setResizingHandle(null);
      return;
    }
    if (isDragging) {
      setIsDragging(false);
      return;
    }
    if (!isDrawing || !startCoords || !mousePosition || !isInsideImage) {
      setIsDrawing(false);
      setStartCoords(null);
      return;
    }
    const { scale, offsetX, offsetY } = canvasTransform;
    const imgX = (mousePosition.x - offsetX) / scale;
    const imgY = (mousePosition.y - offsetY) / scale;

    if (isCoordinateWithinImage(imgX, imgY)) {
      if (
        Math.abs(imgX - startCoords.x) > 5 / canvasTransform.scale &&
        Math.abs(imgY - startCoords.y) > 5 / canvasTransform.scale
      ) {
        const muraTypeData = MURA_TYPE_DATA[selectedMuraType] || MURA_TYPE_DATA["other"];
        const newRectangle = {
          x: Math.min(startCoords.x, imgX),
          y: Math.min(startCoords.y, imgY),
          width: Math.abs(imgX - startCoords.x),
          height: Math.abs(imgY - startCoords.y),
          color: selectedColor,
          muraType: selectedMuraType,
          muraTypeIndex: Number(muraTypeData.id),
        };
        setRectangles((prev) => [...prev, newRectangle]);
        setSelectedRectIndex(rectangles.length);
      }
    }
    setIsDrawing(false);
    setStartCoords(null);
  };

  const handleTouchCancel = (e) => {
    e.preventDefault();
    setIsDrawing(false);
    setStartCoords(null);
  };

  const getCanvasCoordinates = (clientX, clientY) => {
    if (!canvasRef.current) return { x: 0, y: 0 };
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY,
    };
  };


const handleBackNavigation = () => {
  const recordedEntries = location.state?.recordedEntries || [];
  const yoloAnnotations = location.state?.yoloAnnotations || [];

  const recordedRectangles = recordedEntries
    .filter(entry => {
      const isValid = entry.rectangles &&
                      Array.isArray(entry.rectangles) &&
                      entry.muraType &&
                      MURA_TYPE_DATA[entry.muraType] &&
                      entry.rootCause &&
                      typeof entry.rootCause === 'string' &&
                      entry.rootCause.trim() !== '' &&
                      Array.isArray(entry.rootIssues); 
      if (!isValid) {
        console.warn("Skipping invalid recorded entry:", entry);
      }
      return isValid;
    })
    .flatMap(entry => entry.rectangles)
    .filter(rect => {
      const isValid = rect && rect.muraType && MURA_TYPE_DATA[rect.muraType];
      if (!isValid) {
        console.warn("Skipping invalid recorded rectangle:", rect);
      }
      return isValid;
    });

  let storedRectangles = [];
  try {
    const muraData = JSON.parse(localStorage.getItem("muraData") || "{}");
    if (muraData.rectangles && Array.isArray(muraData.rectangles)) {
      storedRectangles = muraData.rectangles.filter(rect => {
        const hasMatchingEntry = recordedEntries.some(entry =>
          entry.muraType === rect.muraType &&
          entry.rootCause &&
          typeof entry.rootCause === 'string' &&
          entry.rootCause.trim() !== '' &&
          Array.isArray(entry.rootIssues) 
        );
        if (!hasMatchingEntry) {
          console.warn("Skipping stored rectangle without matching valid recordedEntry:", rect);
          return false;
        }
        if (!rect || !rect.muraType || !MURA_TYPE_DATA[rect.muraType]) {
          console.warn("Skipping invalid stored rectangle:", rect);
          return false;
        }
        return true;
      });
    }
  } catch (e) {
    console.warn("Failed to parse muraData.rectangles:", e);
  }

  const allRectangles = [...recordedRectangles, ...storedRectangles];

  const updatedRectangles = allRectangles.reduce((acc, rect) => {
    const hash = generateRectHash(rect);
    if (!acc.seenHashes.has(hash)) {
      acc.seenHashes.add(hash);
      acc.rectangles.push({ ...rect });
    } else {
      console.warn("Skipping duplicate rectangle with hash:", hash, rect);
    }
    return acc;
  }, { rectangles: [], seenHashes: new Set() }).rectangles;

  const updatedRecordedEntries = recordedEntries
    .filter(entry => {
      const isValid = entry.muraType &&
                      MURA_TYPE_DATA[entry.muraType] &&
                      entry.rootCause &&
                      typeof entry.rootCause === 'string' &&
                      entry.rootCause.trim() !== '' &&
                      Array.isArray(entry.rootIssues); 
      if (!isValid) {
        console.warn("Skipping invalid recorded entry for updatedRecordedEntries:", entry);
      }
      return isValid;
    })
    .map(entry => ({
      ...entry,
      rectangles: updatedRectangles.filter(rect => rect.muraType === entry.muraType),
    }));

  const muraData = {
    muraType: selectedMuraType,
    rectangles: updatedRectangles,
    imageDimensions,
    yoloAnnotations: [],
    canvasTransform,
    processedRectHashes: Array.from(processedRectHashes),
  };

  localStorage.setItem("muraData", JSON.stringify(muraData));
  localStorage.setItem("muraRectangles", JSON.stringify(updatedRectangles));
  const navigateState = {
    uploadedImage: state?.uploadedImage || "",
    recordedEntries: updatedRecordedEntries, 
    selectedMuraType: selectedMuraType || location.state?.selectedMuraType || "", 
    customMuraType: location.state?.customMuraType || "", 
    customMuraTypes,
    keepState: true,
    fromMuraLabeling: true,
    yoloAnnotations: [],
    muraData,
    analysisResult: location.state?.analysisResult,
    suggestion: state.suggestion || location.state?.suggestion || "",
  };

  navigate("/Re-labeling", {
    state: navigateState,
    replace: true,
  });
};

  return (
    <div className="min-h-screen bg-black text-white flex flex-col items-center p-4 sm:p-6 md:p-8 lg:p-10">
      <div className="w-full flex items-center justify-between mb-6">
        <button onClick={handleBackNavigation}>
          <ArrowLeft className="w-6 h-6 text-white" />
        </button>
        <h2 className="text-xl sm:text-2xl font-bold text-center flex-1">Mark Re-labeling Region</h2>
      </div>
      <div className="w-full mb-6">
        <h4 className="text-sm sm:text-lg font-semibold mb-2">Issue Type</h4>
        <div className="bg-gray-700 px-4 py-2 text-center text-sm sm:text-base">
          {selectedMuraType ? selectedMuraType.replace(/_/g, ' ') : "Not Selected"}
        </div>
      </div>
      <div className="w-full max-w-4xl mx-auto bg-gray-800 overflow-hidden mb-5">
        {imageSrc ? (
          <canvas
            ref={canvasRef}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseOut={handleMouseUp}
            className="w-full h-auto block touch-none"
            style={{
              maxHeight: '70vh',
              minHeight: '40vh',
              backgroundColor: '#333',
              touchAction: 'none',
              userSelect: 'none',
              ...(navigator.userAgent.toLowerCase().includes('mobile') && {
                maxHeight: '60vh',
                minHeight: '30vh',
                padding: '0',
                margin: '0 auto',
              }),
            }}
          />
        ) : (
          <p className="text-red-500 p-4 md:p-2 text-center">No image available.</p>
        )}
      </div>
      <div className="flex gap-4 mb-6">
        <button onClick={handleDelete} className="p-2 hover:bg-gray-600 rounded">
          <Trash2 className="w-6 h-6 text-white" />
        </button>
        <input
          type="color"
          value={selectedColor}
          key={`color-picker-${selectedMuraType}-${selectedRectIndex}`}
          onChange={(e) => {
            const newColor = e.target.value;
            setSelectedColor(newColor);
            if (selectedRectIndex !== null) {
              const updatedRectangles = [...rectangles];
              updatedRectangles[selectedRectIndex] = {
                ...updatedRectangles[selectedRectIndex],
                color: newColor,
              };
              setRectangles(updatedRectangles);
            }
          }}
        />
      </div>
      <div className="w-full flex flex-col items-center mt-8 sm:mt-8 px-1 pb-6 sm:pb-0">
        <div className="flex flex-row justify-between gap-3 w-full max-w-8xl">
          <button
            className="bg-gray-700 text-white px-5 py-2 text-xs sm:px-5 sm:py-2 sm:text-base md:text-lg rounded-lg transition duration-300 w-[28%] sm:w-[52%] md:w-[52%] lg:w-[72%]"
            onClick={handleBackNavigation}
          >
            Back
          </button>
          <button
            onClick={handleNext}
            disabled={!hasRectBeauty}
            className={`px-2 py-2 text-xs sm:px-5 sm:py-2 sm:text-base md:text-lg rounded-lg transition duration-300 w-[50%] sm:w-[60%] md:w-[60%] lg:w-[80%] whitespace-nowrap ${
              hasRectBeauty
                ? "bg-blue-600 hover:bg-blue-800 text-white cursor-pointer"
                : "bg-gray-500 text-gray-300 cursor-not-allowed"
            }`}
          >
            Root Cause Re-Labeling
          </button>
        </div>
      </div>
    </div>
  );
};

export default MarkRelabelingRegion;